import React, { useState } from 'react';
import { User, Lock, ShoppingCart, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import { isValidEmail } from '../../utils/validators';
import './LoginPage.css';

const LoginPage = () => {
  const { login, loading, error, clearError } = useApp();

  const [formData, setFormData] = useState({
    usuario: '',
    password: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);

  // Usuarios de demostración (usando credenciales de la BD)
  const demoUsers = [
    { usuario: 'admin', password: 'password', name: 'Administrador' },
    { usuario: 'cajero', password: 'password', name: 'Cajero' },
    { usuario: 'gerente', password: 'password', name: 'Gerente' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Limpiar error general
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.usuario.trim()) {
      errors.usuario = 'El usuario es requerido';
    }

    if (!formData.password.trim()) {
      errors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 3) {
      errors.password = 'La contraseña debe tener al menos 3 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    console.log('🔐 Intentando login con:', { usuario: formData.usuario });

    const result = await login(formData.usuario, formData.password);

    if (result.success) {
      console.log('✅ Login exitoso');
      // Opcional: descomentar para debug
      // console.log('Usuario:', result.data.nombre);
      // console.log('Almacenes:', result.data.almacenes);
    } else {
      console.log('❌ Login fallido:', result.message);
    }
  };

  const handleDemoLogin = (userType) => {
    const user = demoUsers.find(u => u.name.toLowerCase().includes(userType.toLowerCase()));
    if (user) {
      setFormData({
        usuario: user.usuario,
        password: user.password
      });
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Panel izquierdo - Branding */}
        <div className="login-branding">
          <div className="branding-content">
            <div className="logo">
              <ShoppingCart size={48} />
              <h1>VERTEX</h1>
            </div>
            <p className="branding-subtitle">
              Sistema de Punto de Venta para Abarrotes
            </p>

          </div>
        </div>

        {/* Panel derecho - Formulario */}
        <div className="login-form-panel">
          <div className="login-form-container">
            <div className="login-header">
              <h2>Iniciar Sesión</h2>
              <p>Ingresa tus credenciales para acceder al sistema</p>
            </div>

            {error && (
              <div className="login-error">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="login-form">
              <Input
                label="Usuario"
                type="text"
                placeholder="Ingresa tu usuario"
                value={formData.usuario}
                onChange={(e) => handleInputChange('usuario', e.target.value)}
                error={formErrors.usuario}
                icon={<User size={20} />}
                required
                disabled={loading}
              />

              <Input
                label="Contraseña"
                type="password"
                placeholder="Ingresa tu contraseña"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={formErrors.password}
                icon={<Lock size={20} />}
                required
                disabled={loading}
              />

              <div className="login-options">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="checkmark"></span>
                  Recordarme
                </label>

                <button type="button" className="forgot-password" disabled={loading}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <Button
                type="submit"
                size="large"
                loading={loading}
                disabled={loading}
                className="login-button"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </form>

            {/* Usuarios de demostración */}
            <div className="demo-users">
              <p className="demo-title">Usuarios de demostración:</p>
              <div className="demo-buttons">
                <button
                  type="button"
                  className="demo-btn"
                  onClick={() => handleDemoLogin('administrador')}
                  disabled={loading}
                >
                  Administrador
                </button>
                <button
                  type="button"
                  className="demo-btn"
                  onClick={() => handleDemoLogin('cajero')}
                  disabled={loading}
                >
                  Cajero
                </button>
                <button
                  type="button"
                  className="demo-btn"
                  onClick={() => handleDemoLogin('gerente')}
                  disabled={loading}
                >
                  Gerente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;